import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import {
  GridOptions,
  ICellRendererParams,
  MultiRowSelectionOptions,
} from 'ag-grid-community';
import GridCellErrorBoundary from '../GridCellErrorBoundary';
import Cell from '../V5Gadget/Matrix/Cell';
import Checkbox from '../Inputs/Checkbox';
import Tooltip from '../Tooltip';
import _ from 'lodash';

export const GridSelectedDocumentsContext = React.createContext<{
  selectedDocumentIds: string[];
  setSelectedDocumentIds: React.Dispatch<React.SetStateAction<string[]>>;
  // When true, selection checkboxes are shown but disabled (read-only grid).
  isDisabled?: boolean;
  // Optional explanation shown on hover while the checkboxes are disabled.
  disabledMessage?: string;
  // Optional per-row predicate — return false to disable that row's checkbox.
  // Pair with the grid's `rowSelection.isRowSelectable` so the row also can't
  // be selected via "select all".
  isRowSelectable?: (data: any) => boolean;
  // Optional explanation shown on hover for a row disabled by isRowSelectable.
  // May be a static string, or a per-row function when the reason (and message)
  // varies between rows.
  rowDisabledMessage?: string | ((data: any) => string | undefined);
}>({
  selectedDocumentIds: [],
  setSelectedDocumentIds: window.tokenFunction,
  isDisabled: false,
});

export const ROW_SELECTION_CONFIG: MultiRowSelectionOptions = {
  mode: 'multiRow',
  enableClickSelection: false,
  checkboxes: false,
  headerCheckbox: false,
};

export const ROW_SELECTION_OPTIONS: GridOptions = {
  rowSelection: ROW_SELECTION_CONFIG,
};
export const SELECT_DOCUMENT_CELL = 'SELECT_DOCUMENT_CELL';
export const SELECT_DOCUMENT_HEADER_CELL = 'SELECT_DOCUMENT_HEADER_CELL';

export const SelectDocumentHeaderCell = (params: ICellRendererParams) => {
  const { isDisabled, disabledMessage, isRowSelectable } = useContext(
    GridSelectedDocumentsContext,
  );
  const [isSelected, setIsSelected] = React.useState<boolean>(false);

  // A node the caller has marked non-selectable (via isRowSelectable) must be
  // skipped by "select all" — otherwise the header would select disabled rows.
  const canSelectNode = useCallback(
    (node: { isRowPinned: () => boolean; data: unknown }) => {
      if (node.isRowPinned()) {
        return false;
      }
      return !isRowSelectable || isRowSelectable(node.data) !== false;
    },
    [isRowSelectable],
  );

  const onSelectionChanged = useCallback(() => {
    let isS = true;
    let selectableCount = 0;
    params.api.forEachNode((n) => {
      if (!canSelectNode(n)) {
        return;
      }
      selectableCount += 1;
      if (!n.isSelected()) {
        isS = false;
      }
    });
    // Only show "all selected" when there is at least one selectable row.
    setIsSelected(selectableCount > 0 && isS);
  }, [canSelectNode, params.api]);

  useEffect(() => {
    params.api.addEventListener('selectionChanged', onSelectionChanged);

    // Initial check
    onSelectionChanged();

    return () => {
      if (params.api.isDestroyed()) {
        return;
      }
      params.api.removeEventListener('selectionChanged', onSelectionChanged);
    };
  }, [onSelectionChanged, params.api]);

  const onToggled = useCallback(() => {
    if (isSelected) {
      params.api.deselectAll();
      return;
    }
    // Select only selectable rows rather than api.selectAll(), which ignores
    // isRowSelectable and would select disabled rows.
    params.api.forEachNode((node) => {
      if (canSelectNode(node)) {
        node.setSelected(true);
      }
    });
  }, [canSelectNode, isSelected, params.api]);

  return (
    <Cell>
      <Tooltip content={isDisabled ? disabledMessage : undefined}>
        <Checkbox
          isChecked={isSelected}
          onToggled={onToggled}
          isDisabled={isDisabled}
          testId={'grid-select-all'}
        />
      </Tooltip>
    </Cell>
  );
};

const SelectDocumentCell = (props: ICellRendererParams) => {
  const {
    selectedDocumentIds,
    setSelectedDocumentIds,
    isDisabled,
    disabledMessage,
    isRowSelectable,
    rowDisabledMessage,
  } = useContext(GridSelectedDocumentsContext);

  const isRowDisabled = useMemo(() => {
    if (!isRowSelectable) {
      return false;
    }
    return isRowSelectable(props.node.data) === false;
  }, [isRowSelectable, props.node.data]);

  useEffect(() => {
    props.node.addEventListener('rowSelected', () => {
      setSelectedDocumentIds((c) => {
        if (props.node.isSelected()) {
          return _.uniq([...c, props.node.data.id]);
        }
        return c.filter((id) => id !== props.node.data.id);
      });
    });
  }, [props.node, setSelectedDocumentIds]);

  const documentId = useMemo(() => {
    const id = props.node.data.id;
    if (!id) {
      const e = new Error();
      e.name = 'SelectedDocumentCell: row has no id';
      throw e;
    }

    return id;
  }, [props.node.data.id]);

  const isSelected = useMemo(() => {
    return selectedDocumentIds.includes(documentId);
  }, [documentId, selectedDocumentIds]);

  const onToggled = useCallback(() => {
    props.node.setSelected(!isSelected);
  }, [isSelected, props.node]);

  const resolvedRowDisabledMessage = useMemo(() => {
    if (typeof rowDisabledMessage === 'function') {
      return rowDisabledMessage(props.node.data);
    }
    return rowDisabledMessage;
  }, [rowDisabledMessage, props.node.data]);

  const tooltipContent = useMemo(() => {
    if (isRowDisabled) {
      return resolvedRowDisabledMessage;
    }
    if (isDisabled) {
      return disabledMessage;
    }
    return undefined;
  }, [disabledMessage, isDisabled, isRowDisabled, resolvedRowDisabledMessage]);

  return (
    <Cell>
      <Tooltip content={tooltipContent}>
        <Checkbox
          isChecked={isSelected}
          onToggled={onToggled}
          isDisabled={isDisabled || isRowDisabled}
          testId={`grid-select-${documentId}`}
        />
      </Tooltip>
    </Cell>
  );
};

const Gate = (params: ICellRendererParams) => (
  <GridCellErrorBoundary
    params={params}
    childComponent={<SelectDocumentCell {...params} />}
  />
);

export default Gate;
